<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Contact.translations.json";
	import type { ContactProps } from "./ContactProps";
	import contactHero from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg";
	import contactHeroSm from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=547&imagetools";
	import contactHeroMd from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=600&imagetools";
	import contactHeroLg from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=480&imagetools";
	import contactHeroXl from "../assets/images/contact/vertiflex-ostrava-poruba.jpeg?webp&w=707&imagetools";
	import { nbspify } from "../../../core/utils/nbspify";
	import { Department } from "../../../core/schema/Department";
	import ContactCardWrapper from "../components/Contact/ContactCardWrapper.svelte";
	import type { Contact } from "../../../core/schema/Contact";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { setContext } from "svelte";

	type $$Props = ContactProps;

	const { contacts } = $$restProps as $$Props;

	const translate = getTranslate(translations);

	interface ContactData {
		contacts: Contact[];
		i18nKey: "backoffice" | "management" | "sales" | "serviceAndSupport" | "software";
	}

	$: groupedContacts = [
		{
			contacts: contacts.filter((contact) => contact.department === Department.Management),
			i18nKey: "management",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Sales),
			i18nKey: "sales",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Service),
			i18nKey: "serviceAndSupport",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Software),
			i18nKey: "software",
		},
		{
			contacts: contacts.filter((contact) => contact.department === Department.Backoffice),
			i18nKey: "backoffice",
		},
	] satisfies ContactData[];

	setContext(thumbnailImageContextKey, contactHero);
</script>

<Layout>
	<div class="container">
		<section class="flex flex-col gap-2 lg:mb-8 lg:flex-row">
			<div class="hidden w-full lg:block lg:w-1/2">
				<StaticPicture
					image={contactHeroXl}
					class="relative h-[20rem] rounded-lg sm:h-[25rem] lg:h-full"
					imgClass="rounded-lg h-full w-full object-cover inset-0 max-w-[initial] absolute"
					width={708}
					height={640}
					alt={translate("ilustationPhotoAltText")}
					loading="eager"
					sourcesets={{
						"<md": contactHeroSm,
						">md": contactHeroMd,
						">lg": contactHeroLg,
						">xl": contactHeroXl,
					}}
				/>
			</div>
			<div class="flex flex-col lg:gap-[13rem] lg:p-4 xl:w-1/2 xl:p-8">
				<div class="p-4 pb-0 lg:p-0">
					<h1 class="text-2lg mb-[4.4375rem] p-0 font-normal lg:m-0 lg:text-3xl">{nbspify("VertiFlex s.r.o.")}</h1>
				</div>
				<div class="flex flex-col gap-6 lg:flex-row xl:justify-between">
					<div class="grid grid-cols-2 p-4 pt-0 lg:mt-[-3rem] lg:flex lg:flex-col lg:p-0">
						<div class="flex flex-col">
							<span class="text-3xs md:text-xs lg:mb-0 lg:leading-[1.425rem]">{nbspify("VertiFlex s.r.o.")}</span>
							<a href="https://maps.app.goo.gl/Gu1RUi99UfqBBGdA7" class="mb-4 lg:mb-0">
								<address>
									<span class="text-3xs block not-italic md:text-xs lg:mb-0 lg:leading-[1.425rem]"
										>{nbspify("Gen. Sochora 6176/6A")}</span
									>
									<span class="text-3xs block not-italic md:text-xs lg:mb-0 lg:leading-[1.425rem]"
										>{nbspify("708 00 Ostrava")}</span
									>
									<span class="text-3xs block not-italic md:text-xs lg:mb-8 lg:leading-[1.425rem]"
										>{translate("cz")}</span
									>
								</address>
							</a>
							<span class="text-3xs md:text-xs lg:leading-[1.425rem]">
								{translate("cin")}
								08887489
							</span>
							<span class="text-3xs md:text-xs lg:mb-8 lg:leading-[1.425rem]">
								{translate("vatId")}
								CZ08887489
							</span>
						</div>
						<div class="flex flex-col">
							<div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6">
								<span class="text-3xs mb-4 md:text-xs lg:mb-8 lg:leading-[1.425rem]">
									{translate("phone")}:
								</span>
								<span
									class="text-primary text-3xs col-span-2 mb-4 font-semibold sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-8 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="tel:+420554818554">
										{nbspify("+420 554 818 554")}
									</a>
								</span>

								<span class="text-3xs text-nowrap md:text-xs lg:mb-0 lg:leading-[1.425rem]">E-mail:</span>
								<span
									class="text-primary text-3xs col-span-2 justify-start font-semibold underline sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-0 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="mailto:info@vertiflex.eu">info@vertiflex.eu</a>
								</span>
								<span class="text-3xs md:text-xs lg:mb-0 lg:leading-[1.425rem]">
									{translate("sales")}:
								</span>
								<span
									class="text-primary text-3xs col-span-2 justify-start font-semibold underline sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-0 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="mailto:info@vertiflex.eu">sales@vertiflex.eu</a>
								</span>
								<span class="text-3xs md:text-xs lg:mb-0 lg:leading-[1.425rem]">
									{translate("service")}:
								</span>
								<span
									class="text-primary text-3xs col-span-2 justify-start font-semibold underline sm:col-span-3 md:text-xs lg:col-span-5 lg:mb-0 lg:ml-2 lg:leading-[1.425rem]"
								>
									<a href="mailto:info@vertiflex.eu">service@vertiflex.eu</a>
								</span>
							</div>
						</div>
					</div>
					<StaticPicture
						image={contactHeroXl}
						class="flex h-[16rem] w-full items-center justify-center overflow-hidden rounded-lg sm:h-auto lg:hidden"
						imgClass="w-full rounded-lg object-cover h-full object-center"
						width={304}
						height={192}
						alt={translate("ilustationPhotoAltText")}
						loading="eager"
						sourcesets={{
							"<md": contactHeroSm,
							">md": contactHeroMd,
							">lg": contactHeroLg,
							">xl": contactHeroXl,
						}}
					/>
				</div>
			</div>
		</section>
		<section>
			<div class="mt-12 flex flex-col gap-16 md:gap-20">
				{#each groupedContacts as { i18nKey, contacts }}
					<ContactCardWrapper key={i18nKey} headline={translate(i18nKey)} {contacts} />
				{/each}
			</div>
		</section>
	</div>
</Layout>
